import {Component, OnDestroy, OnInit} from "@angular/core";
import {ActivatedRoute, Params, Router} from "@angular/router";
import {UserService} from "./user.service";
import {RouteHelper, RouteMapping} from "../misc/route-helper";
import {AppSettingsService} from "../app-settings/app-settings.service";
import {Subscription} from "rxjs";
import {handleErrors} from "../misc/handle-errors.decorator";

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_LIMIT = 10;

@Component({
  selector: "user-list",
  templateUrl: "./users.template.pug",
  providers: [
    UserService,
  ],
})

export class UserListComponent extends RouteHelper implements OnInit, OnDestroy {
  users: any[] = [];
  currentPage: number = DEFAULT_PAGE;
  pageSize: number = DEFAULT_PAGE_LIMIT;
  maxPages: number;
  totalCount: number = 0;
  searchText: string = "";
  error: Error;
  isManager: boolean = false;
  isSwitched: boolean = false;
  isAllowedToEditUsers: boolean = false;
  uid: string = "";
  showSwitchColumn: boolean = false;
  private canSwitchAll: boolean = false;
  private userSubscription: Subscription;

  public paramsMap: RouteMapping[] = [
    {fieldName: "currentPage", paramName: "page", defaultValue: DEFAULT_PAGE, type: "number"} as RouteMapping,
    {fieldName: "pageSize", paramName: "limit", defaultValue: DEFAULT_PAGE_LIMIT, type: "number"} as RouteMapping,
    {fieldName: "searchText", paramName: "search"} as RouteMapping,
  ];

  constructor(
    private userService: UserService,
    public route: ActivatedRoute,
    public router: Router,
    private appSettingsService: AppSettingsService,
  ) {
    super();
  }

  ngOnInit() {
    super.ngOnInit();

    this.userSubscription = this.appSettingsService.user.subscribe((user) => {
      this.isManager = user.privilegeFlags["manager"];
      this.canSwitchAll = user.privilegeFlags["switch-user"];
      this.uid = user.uid;
      this.isSwitched = user.isSwitched;

      this.isAllowedToEditUsers = user.privilegeFlags["manage-service-accounts"] ||
        user.privilegeFlags["manage-user-accounts"];
    });
  }

  ngOnDestroy() {
    if (this.userSubscription && !this.userSubscription.closed) {
      this.userSubscription.unsubscribe();
    }
  }

  public navigate(queryParams: Params, skipLocationChange: boolean = false): Promise<boolean> {
    return this.router.navigate(["users"], {queryParams, skipLocationChange});
  }

  @handleErrors()
  async loadData(): Promise<void> {
    this.error = undefined;

    const {rows, count} = await this.userService.getUsers(
      (+this.currentPage) - 1,
      (+this.pageSize),
      this.searchText,
    ).toPromise();

    this.users = rows;
    this.users.forEach((user) => {
      user.canSwitch = !this.isSwitched && !user.isCurrentUser &&
        (
          this.canSwitchAll ||
          (this.isManager && user.managers && user.managers.includes(this.uid))
        );
    });

    this.showSwitchColumn = this.users.some(it => it.canSwitch);

    this.maxPages = Math.ceil(count / (+this.pageSize));
    this.totalCount = count;
  }

  public switchUser(user): void {
    this.userService.switchUser(user)
      .then(() => { document.location.reload(true); });
  }

  public onListControlsChange(event) {
    switch (event["key"]) {
      case "page":
        this.currentPage = event["value"];
        break;
      case "search":
        this.currentPage = DEFAULT_PAGE;
        this.searchText = event["value"];
        break;
      case "limit":
        this.currentPage = DEFAULT_PAGE;
        this.pageSize = event["value"];
        break;
      default:
        console.info(`Unknown ${event["key"]}`);
        break;
    }
    this.syncRoute();
  }
}
